import { useEffect, useMemo, useCallback, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Snackbar from "@mui/material/Snackbar";
import Alert from "@mui/material/Alert";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import DashboardLayout from "common/LayoutContainers/DashboardLayout";
import DashboardNavbar from "common/Navbars/DashboardNavbar";
import Footer from "common/Footer";
import DataTable from "common/Tables/DataTable";
import reviewRequestsTableData from "layouts/tables/data/reviewRequestsTableData";
import {
    getPendingReviews,
    approveReview,
    rejectReview,
    reviewRequestsActions,
} from "store/slice/reviewRequestsSlice";
import { requestsCountAction } from "store/slice/infoSlice";

function ReviewRequests() {
    const dispatch = useDispatch();
    const location = useLocation();
    const { reviews, total, success, error } = useSelector((store) => store.reviewRequests);

    const [page, setPage] = useState(0);
    const [limit, setLimit] = useState(25);

    // Rejection dialog state
    const [rejectDialog, setRejectDialog] = useState({ open: false, mistakeId: null });
    const [rejectReason, setRejectReason] = useState("");

    const fetchReviews = useCallback(() => {
        dispatch(getPendingReviews({ page: page + 1, limit }));
    }, [dispatch, page, limit]);

    useEffect(() => {
        fetchReviews();
    }, [fetchReviews, location.key]);

    const handleApprove = useCallback(async (id) => {
        await dispatch(approveReview(id));
        fetchReviews();
        dispatch(requestsCountAction());
    }, [dispatch, fetchReviews]);

    const handleRejectClick = useCallback((id) => {
        setRejectReason("");
        setRejectDialog({ open: true, mistakeId: id });
    }, []);

    const handleRejectClose = () => {
        setRejectDialog({ open: false, mistakeId: null });
        setRejectReason("");
    };

    const handleRejectConfirm = async () => {
        await dispatch(rejectReview({ id: rejectDialog.mistakeId, rejectReason }));
        handleRejectClose();
        fetchReviews();
        dispatch(requestsCountAction());
    };

    const handleSnackbarClose = () => dispatch(reviewRequestsActions.resetStatus());

    const { columns, rows } = useMemo(
        () => reviewRequestsTableData({ reviews, onApprove: handleApprove, onRejectClick: handleRejectClick }),
        [reviews, handleApprove, handleRejectClick]
    );

    return (
        <DashboardLayout>
            <DashboardNavbar />
            <MDBox pt={6} pb={3}>
                <Grid container spacing={6}>
                    <Grid item size={{ xs: 12 }}>
                        <Card>
                            <MDBox
                                mx={2}
                                mt={-3}
                                py={3}
                                px={2}
                                variant="gradient"
                                bgColor="info"
                                borderRadius="lg"
                                coloredShadow="info"
                                display="flex"
                                justifyContent="space-between"
                                alignItems="center"
                            >
                                <MDTypography variant="h6" color="white">
                                    Review Requests
                                </MDTypography>
                                <MDTypography variant="caption" color="white" sx={{ opacity: 0.85 }}>
                                    {total} pending
                                </MDTypography>
                            </MDBox>
                            <MDBox pt={3}>
                                <DataTable
                                    table={{ columns, rows }}
                                    isSorted={false}
                                    showTotalEntries={false}
                                    entriesPerPage={{ defaultValue: limit, entries: [10, 25, 50, 100] }}
                                    noEndBorder
                                    manualPagination
                                    pageCount={Math.ceil(total / limit)}
                                    currentPage={page}
                                    onPageChange={setPage}
                                    onPageSizeChange={(newLimit) => {
                                        setLimit(newLimit);
                                        setPage(0);
                                    }}
                                />
                            </MDBox>
                        </Card>
                    </Grid>
                </Grid>
            </MDBox>
            <Footer />

            {/* Rejection reason dialog */}
            <Dialog
                open={rejectDialog.open}
                onClose={handleRejectClose}
                maxWidth="sm"
                fullWidth
                slotProps={{ paper: { sx: { borderRadius: "16px", p: 1, overflow: "hidden" } } }}
            >
                <DialogTitle sx={{ fontWeight: 700, fontSize: "1.1rem", pb: 0 }}>
                    Reject Mistake Review
                </DialogTitle>
                <DialogContent sx={{ pt: "12px !important", overflowX: "hidden" }}>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Are you sure you want to reject this mistake? Please provide a reason below.
                    </Typography>
                    <TextField
                        autoFocus
                        fullWidth
                        multiline
                        rows={3}
                        label="Reason for Rejection"
                        value={rejectReason}
                        onChange={(e) => setRejectReason(e.target.value)}
                        placeholder="Describe why this mistake review is being rejected…"
                        slotProps={{ htmlInput: { maxLength: 1000 } }}
                    />
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
                    <Button onClick={handleRejectClose} variant="text"
                        sx={{ borderRadius: "20px", textTransform: "none", fontWeight: 600, color: "text.secondary" }}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleRejectConfirm}
                        disabled={!rejectReason.trim()}
                        variant="contained"
                        disableElevation
                        sx={{
                            borderRadius: "20px",
                            textTransform: "none",
                            fontWeight: 600,
                            background: "linear-gradient(135deg, #e74c3c 0%, #c0392b 100%)",
                            color: "#fff",
                            boxShadow: "0 2px 8px rgba(231,76,60,0.35)",
                            "&:hover": {
                                background: "linear-gradient(135deg, #c0392b 0%, #96281b 100%)",
                                boxShadow: "0 4px 14px rgba(231,76,60,0.45)",
                            },
                            "&.Mui-disabled": {
                                background: "#e0e0e0",
                                color: "#9e9e9e",
                                boxShadow: "none",
                            },
                        }}
                    >
                        Confirm Reject
                    </Button>
                </DialogActions>
            </Dialog>

            <Snackbar
                open={success.status}
                autoHideDuration={3000}
                onClose={handleSnackbarClose}
                anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
            >
                <Alert onClose={handleSnackbarClose} severity="success" sx={{ width: "100%" }}>
                    {success.message}
                </Alert>
            </Snackbar>
            <Snackbar
                open={error.status}
                autoHideDuration={4000}
                onClose={handleSnackbarClose}
                anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
            >
                <Alert onClose={handleSnackbarClose} severity="error" sx={{ width: "100%" }}>
                    {error.message}
                </Alert>
            </Snackbar>
        </DashboardLayout>
    );
}

export default ReviewRequests;
